import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { useToasts } from '../../hooks/useToasts';
import { errorMessage } from '../../lib/errors';
import { formatDateTime } from '../../lib/format';
import { Button } from '../../components/primitives';
import { Icon } from '../../components/Icon';
import { SyncStatusIndicator } from '../../components/SyncStatusIndicator';
import { findNavItem, NAV_GROUP_LABEL } from '../../routes/navigation';

export function Header(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { push } = useToasts();
  const pendingRequests = useIsFetching();

  const current = findNavItem(location.pathname);

  const refreshAll = (): void => {
    void queryClient.invalidateQueries();
    push({
      kind: 'info',
      title: 'Actualizando',
      message: 'Se volvieron a consultar los datos de la pantalla.',
    });
  };

  return (
    <header className="topbar">
      <nav className="breadcrumb" aria-label="Ruta de navegación">
        <Link to="/">Inicio</Link>
        {current !== undefined && current.path !== '/' && (
          <>
            <span className="breadcrumb__sep" aria-hidden="true">
              /
            </span>
            <span>{NAV_GROUP_LABEL[current.group]}</span>
            <span className="breadcrumb__sep" aria-hidden="true">
              /
            </span>
            <span className="breadcrumb__current" aria-current="page">
              {current.label}
            </span>
          </>
        )}
      </nav>

      <span className="topbar__spacer" />

      <div className="topbar__actions">
        <SyncStatusIndicator />

        <Button
          variant="icon"
          onClick={refreshAll}
          aria-label="Actualizar datos"
          title="Volver a consultar los datos"
          loading={pendingRequests > 0}
        >
          {pendingRequests > 0 ? null : <Icon name="refresh" size={17} />}
        </Button>

        <Button
          variant="icon"
          onClick={toggleTheme}
          aria-label={theme === 'light' ? 'Activar modo oscuro' : 'Activar modo claro'}
          title={theme === 'light' ? 'Modo oscuro' : 'Modo claro'}
        >
          <Icon name={theme === 'light' ? 'moon' : 'sun'} size={17} />
        </Button>

        <UserMenu
          onLogout={async () => {
            try {
              await logout();
            } catch (error) {
              push({ kind: 'error', title: 'No se pudo cerrar la sesión', message: errorMessage(error) });
            } finally {
              navigate('/login', { replace: true });
            }
          }}
          fullName={user?.fullName ?? ''}
          email={user?.email ?? ''}
          roles={user?.roles ?? []}
          lastLoginAt={user?.lastLoginAt ?? null}
        />
      </div>
    </header>
  );
}

interface UserMenuProps {
  fullName: string;
  email: string;
  roles: string[];
  lastLoginAt: string | null;
  onLogout: () => Promise<void>;
}

function UserMenu({ fullName, email, roles, lastLoginAt, onLogout }: UserMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const initials = fullName
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => part[0]?.toLocaleUpperCase('es-CL') ?? '')
    .join('');

  return (
    <div className="popover" ref={containerRef}>
      <button
        type="button"
        className="btn btn--sm"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span
          aria-hidden="true"
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            background: 'var(--teal)',
            color: '#06231f',
            display: 'grid',
            placeItems: 'center',
            fontSize: 10.5,
            fontWeight: 800,
          }}
        >
          {initials.length > 0 ? initials : <Icon name="user" size={12} />}
        </span>
        <span className="nowrap">{fullName.length > 0 ? fullName : 'Mi cuenta'}</span>
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={12} />
      </button>

      {open && (
        <div className="popover__panel" role="menu" aria-label="Menú de usuario">
          <div className="popover__head">
            <strong style={{ fontSize: 12.5 }}>{fullName}</strong>
            <p className="tiny muted">{email}</p>
            {roles.length > 0 && <p className="tiny muted">Rol: {roles.join(', ')}</p>}
            {lastLoginAt !== null && (
              <p className="tiny muted">Último acceso: {formatDateTime(lastLoginAt)}</p>
            )}
          </div>
          <Link className="popover__item" role="menuitem" to="/cambiar-password" onClick={() => setOpen(false)}>
            <Icon name="key" size={14} />
            Cambiar contraseña
          </Link>
          <div className="popover__sep" />
          <button
            type="button"
            role="menuitem"
            className="popover__item"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void onLogout().finally(() => setBusy(false));
            }}
          >
            <Icon name="logout" size={14} />
            Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}
